import { motion } from "framer-motion";
import { ArrowLeft, ShieldCheck, Database, Mail, CreditCard, Server, Clock, UserCheck } from "lucide-react";
import { LogoMark } from "@/components/Logo";

const EASE = [0.16, 1, 0.3, 1];

const SECTIONS = [
  {
    icon: UserCheck,
    title: "1. Responsable du traitement",
    body: "Le site pokebastos06 (ci-après « le Site ») est édité par Sébastien Grosso, artiste indépendant — créations 100% personnalisées, faites en France. Contact : sebastiengrosso.13@gmail.com.",
  },
  {
    icon: Database,
    title: "2. Données collectées",
    body: "Via le formulaire de commande : prénom, nom, lieu (ville/pays), adresse email, vos passions ou univers préférés, le personnage ou sujet souhaité, votre budget, votre message, et éventuellement une photo d'inspiration que vous téléchargez. Via le paiement : les informations de transaction traitées par Stripe (montant, référence de commande). Aucune donnée de carte bancaire ne transite par nos systèmes : le paiement est intégralement géré par Stripe.",
  },
  {
    icon: ShieldCheck,
    title: "3. Finalités et bases légales",
    body: "Vos données servent à : répondre à vos demandes de commande ou de devis et assurer le suivi de votre création (exécution du contrat) ; envoyer vos confirmations de commande par email (exécution du contrat) ; établir les factures et respecter les obligations légales et fiscales (obligation légale). Aucune donnée n'est utilisée pour de la publicité, et aucune n'est vendue ni cédée à des tiers.",
  },
  {
    icon: Clock,
    title: "4. Durée de conservation",
    body: "Les demandes non suivies de commande sont conservées au maximum 3 ans à compter du dernier contact. Les commandes et justificatifs associés sont conservés les durées imposées par le droit français (notamment 10 ans pour les pièces comptables). Vous pouvez demander la suppression de vos données à tout moment (hors obligations légales).",
  },
  {
    icon: Server,
    title: "5. Sous-traitants et transferts",
    body: "Le Site est hébergé par Emergent (hébergement applicatif et base de données). Les paiements sont traités par Stripe, Inc. et ses filiales (paiement, prévention de la fraude, calcul de taxes), qui peut opérer hors de l'Union européenne dans le cadre de garanties contractuelles adaptées. Les emails transactionnels sont envoyés via le service email géré par Emergent. Ces prestataires ne reçoivent que les données strictement nécessaires.",
  },
  {
    icon: Mail,
    title: "6. Vos droits",
    body: "Vous disposez des droits d'accès, de rectification, d'effacement, de limitation, d'opposition et de portabilité de vos données. Pour exercer vos droits, écrivez à sebastiengrosso.13@gmail.com — une réponse vous sera apportée dans un délai d'un mois. Si vous estimez que vos droits ne sont pas respectés, vous pouvez introduire une réclamation auprès de la CNIL (www.cnil.fr).",
  },
  {
    icon: Database,
    title: "7. Cookies",
    body: "Le Site ne dépose aucun cookie publicitaire ni traceur de mesure d'audience. Seuls des éléments strictement nécessaires à son fonctionnement peuvent être stockés localement sur votre appareil.",
  },
  {
    icon: ShieldCheck,
    title: "8. Sécurité",
    body: "Le Site transite en HTTPS, les photos d'inspiration sont stockées de manière non référençable, et l'accès aux données est limité au strict nécessaire. Aucun mot de passe ni aucune donnée de carte bancaire ne vous sera jamais demandé par email.",
  },
];

export default function PrivacyPage() {
  return (
    <div className="relative min-h-screen overflow-x-clip bg-[#08070d] text-[#f4f3f8] antialiased">
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute -top-24 left-1/4 h-[400px] w-[400px] rounded-full bg-fuchsia-600/15 blur-[140px]" />
        <div className="absolute top-1/2 -right-24 h-[350px] w-[350px] rounded-full bg-cyan-500/15 blur-[140px]" />
      </div>

      <header className="relative border-b border-white/10">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-5 py-5 md:px-8">
          <a href="/" className="group flex items-center gap-3" aria-label="Retour à l'accueil">
            <LogoMark className="h-9 w-9" />
            <span className="font-display text-lg font-extrabold tracking-wide text-white">
              POKE<span className="text-holo">BASTOS</span>
            </span>
          </a>
          <a
            href="/"
            data-testid="privacy-back-link"
            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm font-medium text-[#c8c4d8] transition-all duration-300 hover:border-cyan-400/50 hover:text-white"
          >
            <ArrowLeft size={15} />
            Retour au site
          </a>
        </div>
      </header>

      <main className="relative mx-auto max-w-4xl px-5 py-14 md:px-8 md:py-20">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
          <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.3em] text-cyan-300">Confidentialité</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
            Politique de <span className="text-holo">confidentialité</span>
          </h1>
          <p className="mt-5 text-sm leading-relaxed text-[#a09bb5] sm:text-base">
            La confidentialité de vos données est importante : cette page explique simplement
            quelles informations sont collectées quand vous commandez une carte ou un tableau, à
            quoi elles servent, et quels sont vos droits. Dernière mise à jour : octobre 2025.
          </p>
        </motion.div>

        <div className="mt-12 space-y-5">
          {SECTIONS.map((s, i) => (
            <motion.section
              key={s.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: 0.55, ease: EASE, delay: Math.min(i, 3) * 0.06 }}
              data-testid={`privacy-section-${i + 1}`}
              className="rounded-2xl border border-white/10 bg-[#100e19] p-6 sm:p-8"
            >
              <div className="flex items-center gap-3.5">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-gradient-to-tr from-fuchsia-500/15 to-cyan-500/15">
                  <s.icon size={18} className="text-amber-300" />
                </span>
                <h2 className="font-display text-lg font-bold text-white sm:text-xl">{s.title}</h2>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-[#c8c4d8] sm:text-[15px]">{s.body}</p>
            </motion.section>
          ))}
        </div>

        <p className="mt-10 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-[#6c6685]">
          © {new Date().getFullYear()} Pokebastos Custom Card — Tous droits réservés
        </p>
      </main>
    </div>
  );
}
